import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { captureAndStripPartnerToken } from '@sighthost/config';

/**
 * Captures the partner acquisition token (`?pt=`) at the PORTAL ROOT, on every
 * navigation, then strips it from the URL.
 *
 * ── Why the root and not the order page (2026-09-25 review, F1) ──────────
 * Until now only `NewServicePage` captured `?pt=`. The marketing site appends
 * the token to EVERY portal-bound CTA — /sign-up, /sign-in,
 * /app/domains/register, /app/order/services/* — and each of those either
 * ignored it or bounced through `ProtectedRoute` → `/sign-in` before the page
 * that knew about it could mount. A visitor who came in through a partner
 * link and did anything but "buy hosting as a guest" was silently lost to the
 * partner, with nothing in the data looking wrong.
 *
 * Mounted above every route (inside the router, beside AuthProvider), so the
 * capture runs BEFORE any guard's redirect can discard the query string.
 *
 * ── CT-8 ─────────────────────────────────────────────────────────────────
 * The token lives in sessionStorage (dies with the tab) and is removed from
 * the URL immediately, so a bookmark or a restored tab cannot re-seed a later
 * direct visit. `replace` keeps it out of history. Every other param survives.
 *
 * Renders nothing.
 */
export function PartnerTokenCapture() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const stripped = captureAndStripPartnerToken(location.search);
    if (stripped === null) return; // no token in the URL — nothing to rewrite
    const search = stripped.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  return null;
}
